import { ReleaseType, DATE_FORMATS } from '@callmart/types';
import { integerComparator } from '../components/AgGrid/comparators';
import { ReleaseTypeText } from '../types/StrategicVolumeSummary';
import { ReleasedTypeText } from '../types/CorrectedTotalSummary';
import { OfferedReportStatusText } from '../types/OfferedReportSummary';
import { parseLocalizedNumber } from './numberUtils';
import { formatSlotTime, formatSlotDay, formatDateOnly } from '@utils/dayjs';

/**
 * Parse a cell value from AG Grid, handling localized number formats.
 *
 * SEMANTIC CONTRACT (critical for deletion and validation):
 * - Returns `null`: User cleared the field → DELETION signal for backend
 * - Returns `undefined`: Invalid input (parsing error) → SHOW ERROR to user
 * - Returns `number`: Valid parsed number → UPDATE value
 *
 * USAGE IN VALUE SETTERS:
 * ```
 * valueSetter: (params) => {
 *   const parsed = parseCellValue(params.newValue);
 *
 *   // NEVER convert null to undefined - this breaks deletion!
 *   params.data[field] = parsed;
 *   return true;
 * }
 *
 * // In validation handler:
 * if (parsed === undefined) {
 *   toast.error('Enter a valid number');
 *   return; // Reject invalid input
 * }
 * // Allow null (deletion) and numbers (update) to proceed
 * ```
 *
 * @param {number | string | null | undefined} value - Raw cell value from AG Grid
 * @returns {undefined | number | null}
 *   - `undefined`: Invalid input (parsing error)
 *   - `null`: Empty/cleared field (deletion signal)
 *   - `number`: Valid parsed number
 */
export const parseCellValue = (value) => {
    if (value === null || value === '') return null; // Deletion
    if (value === undefined) return undefined; // Pass-through

    if (typeof value === 'number') {
        return value; // Already parsed
    }
    const parsed = parseLocalizedNumber(value);

    return parsed === null ? undefined : parsed; // Invalid → undefined
};

export const createTableRows = (rowsMap, firstColumnField) => {
    let rows = [];

    for (const key of rowsMap.keys()) {
        const row = {
            [firstColumnField]: key,
            ...rowsMap.get(key),
        };

        rows = [...rows, row];
    }

    return rows;
};

export const addRowsWithStaticFieldNameInMap = (list, rowsMap, field, value) => {
    for (let i = 0; i < list.length; i++) {
        const key = getSlotTimeRowKey(list[i].slotDate);
        if (rowsMap.has(key)) {
            rowsMap.set(key, {
                ...rowsMap.get(key),
                [`${field}`]: list[i][value] ? list[i][value] : 0,
            });
        } else {
            rowsMap.set(key, {
                [`${field}`]: list[i][value] ? list[i][value] : 0,
            });
        }
    }

    return rowsMap;
};

export const addRowsWithDynamicFieldNameInMap = (list, rowsMap, field, value) => {
    for (let i = 0; i < list.length; i++) {
        const key = getSlotTimeRowKey(list[i].slotDate);
        if (rowsMap.has(key)) {
            rowsMap.set(key, {
                ...rowsMap.get(key),
                [`${field}${list[i][field]}`]: list[i][value],
            });
        } else {
            rowsMap.set(key, {
                [`${field}${list[i][field]}`]: list[i][value],
            });
        }
    }

    return rowsMap;
};

export const getSlotTimeRowKey = (slotDate, displaySeconds = false) => {
    return formatSlotTime(slotDate, displaySeconds ? DATE_FORMATS.TIME_WITH_SECONDS : DATE_FORMATS.TIME);
};

export const combineChartDataInOneArray = (bookedCalls, forecastDeltaCalls) => {
    const result = forecastDeltaCalls.map((forecastDeltaCall) => {
        const itemInBookedCalls = bookedCalls.find((bookedCall) => bookedCall.slotDate === forecastDeltaCall.slotDate);
        const formattedDate = formatSlotTime(forecastDeltaCall.slotDate, 'DD.MM');
        const slotInterval = new Date(forecastDeltaCall.slotDate).getTime();
        const slotDate = formatSlotTime(forecastDeltaCall.slotDate, DATE_FORMATS.DATETIME);
        if (itemInBookedCalls) {
            return {
                ...forecastDeltaCall,
                ...itemInBookedCalls,
                formattedDate,
                slotInterval,
                slotDate,
            };
        } else {
            return {
                ...forecastDeltaCall,
                bookedBookings: '0',
                formattedDate,
                slotInterval,
                slotDate,
            };
        }
    });

    return result;
};

export const isEmptyChartData = (chartData) => !chartData?.length;

export const isEmptyTableData = (tableData) => {
    const { latesBookedBookingsList, oldestBookedBookingsList } = tableData;

    return !latesBookedBookingsList?.length && !oldestBookedBookingsList?.length;
};

export const areEmptyTotals = (totals) => {
    const { totalForecastDeltas, totalBookedBookings, totalAvailableBookings } = totals;

    return !totalForecastDeltas && !totalBookedBookings && !totalAvailableBookings;
};

export const checkIfNoDashboardDataIsAvailable = (data, dispatchAlert) => {
    if (isEmptyChartData(data.chartData) && isEmptyTableData(data.tableData) && areEmptyTotals({ ...data.totals })) {
        return dispatchAlert({
            message: 'No data available',
            type: 'error',
        });
    }
};

export const checkIsTableRowExpanded = (expandedNode, tableRowNode) => {
    if (expandedNode.key !== tableRowNode.key) return false;
    if (expandedNode.level !== 0 && expandedNode.level !== 1 && expandedNode.level !== 2) return false;

    if (expandedNode.level === 1) return expandedNode?.parent?.key === tableRowNode?.parent?.key;
    else if (expandedNode.level === 2) return expandedNode?.parent?.parent?.key === tableRowNode?.parent?.parent?.key;

    return true;
};

export const createColumnDefsFromSlotDays = (slotDaysInMonth) => {
    let columns = [];

    const headerComponentParams = {
        template:
            '<div class="ag-cell-label-container" role="presentation">' +
            '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
            '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
            '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
            '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
            '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
            '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
            '    <span ref="eText" class="ag-header-cell-text" role="columnheader" style="white-space: pre-line;"></span>' +
            '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
            '  </div>' +
            '</div>',
    };

    columns = [
        ...columns,
        {
            field: 'slotTime',
            headerName: 'Datum Intervall',
            maxWidth: 140,
            cellClass: 'grid-cell-centered',
            pinned: 'left',
        },
    ];

    for (let i = 0; i < slotDaysInMonth.length; i++) {
        const dayName = new Date(slotDaysInMonth[i].date).toLocaleString('en-us', { weekday: 'short' });
        columns = [
            ...columns,
            {
                field: `day${formatSlotDay(slotDaysInMonth[i].date)}`,
                headerName: `${formatSlotTime(slotDaysInMonth[i].date, 'DD.MM')}\n${dayName}`,
                minWidth: 75,
                cellClass: 'grid-cell-centered',
                wrapText: true,
                headerComponentParams: headerComponentParams,
                resizable: true,
                comparator: (valueA, valueB) => {
                    return integerComparator({ valueA, valueB });
                },
            },
        ];
    }

    return columns;
};

export const addRowsWithDayNamesAsColumnsInMap = (list, rowsMap, calculateTotals, propertyName) => {
    const totals = {};
    for (let i = 0; i < list.length; i++) {
        const key = getSlotTimeRowKey(list[i].slotDate, true);
        const day = formatSlotDay(list[i].slotDate);

        if (calculateTotals) {
            totals[`day${day}`]
                ? (totals[`day${day}`] += list[i][propertyName])
                : (totals[`day${day}`] = list[i][propertyName]);
        }

        if (rowsMap.has(key)) {
            rowsMap.set(key, {
                ...rowsMap.get(key),
                [`day${day}`]: list[i][propertyName].toString(),
            });
        } else {
            rowsMap.set(key, {
                [`day${day}`]: list[i][propertyName].toString(),
            });
        }
    }

    if (calculateTotals && list.length) rowsMap.set('Gesamt', totals);

    return rowsMap;
};

// Acceptable date formats: DD.MM.YYYY; DD/MM/YYYY; DD-MM-YYYY
const monthToComparableNumber = (date) => {
    if (date === undefined || date === null || date.length !== 10) {
        return null;
    }
    const yearNumber = parseInt(date.substring(6, 10));
    const monthNumber = parseInt(date.substring(3, 5));
    const dayNumber = parseInt(date.substring(0, 2));
    return yearNumber * 10000 + monthNumber * 100 + dayNumber;
};

export const dateComparator = (date1, date2) => {
    const date1Number = monthToComparableNumber(date1);
    const date2Number = monthToComparableNumber(date2);
    if (date1Number === null && date2Number === null) {
        return 0;
    }
    if (date1Number === null) {
        return -1;
    }
    if (date2Number === null) {
        return 1;
    }
    return date1Number - date2Number;
};

export const formatDateInCell = ({ value }) => formatDateOnly(value);

export const formatTimeInCell = ({ value }) => formatSlotTime(value);

export const getFormattedDateAndTime = (value) => [formatDateInCell({ value }), formatTimeInCell({ value })];

export const timeComparator = (date1, date2) => {
    const localTime1 = formatTimeInCell({ value: date1 });
    const localTime2 = formatTimeInCell({ value: date2 });

    return localTime1.localeCompare(localTime2);
};

export const getValueFromParamsDataIfKeyPresent = (params, key) => {
    if (params.data && params.data[key]) {
        return params?.data[key].value;
    }

    return null;
};

export const getValueForMonthColumn = (params, monthColumn) => {
    return getValueFromParamsDataIfKeyPresent(params, `${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`);
};

export const getSlotTimeColumnDef = ({
    field = 'slotTime',
    headerName = '',
    maxWidth = 50,
    footerRowText = '',
} = {}) => {
    return {
        field,
        headerName,
        maxWidth,
        cellClass: 'grid-cell-centered',
        pinned: 'left',
        valueGetter: (params) => {
            return params.node.footer ? footerRowText : params.data.slotTime;
        },
        editable: false,
    };
};

export const getCellValue = (params, colName, secondColName) => {
    if (params.value) return `${params.value[colName]} ${secondColName ? ` - ${params.value[secondColName]}` : ''}`;
    return '';
};

export const getFooterRowStyle = (params) => {
    if (params.node.footer) {
        return { fontWeight: 'bold' };
    }
};

export const numberColorCellStyleRenderer = ({ value }) => ({
    color: value > 0 ? 'green' : value < 0 ? 'red' : 'black',
});

export const percentageCellStyleRenderer = ({ value, maxValue }) => {
    if (!value && value !== 0) return undefined;
    let percentage = (parseInt(value, 10) || 0) / (maxValue || 100);
    percentage = percentage < 0 ? 0 : percentage;
    return {
        // backgroundColor: `rgba(0, 102, 255, ${percentage > 1 ? 1 : percentage})`, // #0066FF
        background: `linear-gradient(0, rgba(0, 102, 255, ${percentage > 1 ? 1 : percentage})) #fff`, // #0066FF
    };
};

export const ninetiethPercentileCellStyleRenderer = ({ value }) => {
    if (!value) return undefined;
    const percentage = parseInt(value, 10) || 0;
    let backgroundColor = `#ffffff`;
    if (percentage >= 95) {
        backgroundColor = '#59c2c9';
    } else if (percentage >= 90) {
        backgroundColor = '#eac344';
    } else if (percentage >= 1) {
        backgroundColor = '#e66c64';
    }

    return {
        backgroundColor,
    };
};

// Function to generate a consistent color from a string
export const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = hash % 360; // Hue value (0-360 for HSL)
    return {
        backgroundColor: `hsl(${h}, 70%, 80%)`, // Light pastel colors
        color: `hsl(${h}, 70%, 20%)`, // Dark pastel colors
    };
};

export const BadgeStyleRenderer = ({ value, color, backgroundColor, key }) => {
    return (
        <span
            key={key}
            style={{
                color,
                backgroundColor,
                borderRadius: '8px',
                padding: '4px 8px',
                marginRight: '2px',
                display: 'inline-block',
                lineHeight: 1.2,
            }}
        >
            {value}
        </span>
    );
};

// Custom cell renderer for coloring names
export const ColorizedNameRenderer = ({ value, emptyValue }) => {
    const text = value || emptyValue || '';
    return Array.isArray(text) ? (
        <>{text.map((item, index) => BadgeStyleRenderer({ value: item, ...stringToColor(item), key: index }))}</>
    ) : (
        BadgeStyleRenderer({
            value: text,
            ...stringToColor(text),
        })
    );
};

// Custom cell renderer for coloring Succesfull status
export const OfferedReportStatusCellRenderer = ({ value }) => {
    return BadgeStyleRenderer({
        value,
        color: value === OfferedReportStatusText.SUCCESS ? '#ffffff' : '#600000',
        backgroundColor: value === OfferedReportStatusText.SUCCESS ? `#067429` : '#ffadad',
    });
};

export const StrategicVolumeStatusCellRenderer = ({ value }) => {
    switch (value) {
        case ReleaseType.NOT_RELEASED:
        case ReleaseTypeText.NOT_RELEASED:
            return BadgeStyleRenderer({ value, color: '#600000', backgroundColor: '#ffadad' });
        case ReleaseType.READY_FOR_RELEASE:
        case ReleaseTypeText.READY_FOR_RELEASE:
            return BadgeStyleRenderer({ value, color: '#5c2000', backgroundColor: '#ffbd83' });
        case ReleaseType.RELEASED:
        case ReleaseTypeText.RELEASED:
            return BadgeStyleRenderer({ value, color: '#ffffff', backgroundColor: '#067429' });
        default:
            return undefined;
    }
};

export const CorrectedTotalsStatusCellRenderer = ({ value }) => {
    switch (value) {
        case undefined:
        case ReleasedTypeText.UNDEFINED:
            return BadgeStyleRenderer({ value, color: '#600000', backgroundColor: '#ffadad' });
        case false:
        case ReleasedTypeText.NOT_RELEASED:
            return BadgeStyleRenderer({ value, color: '#5c2000', backgroundColor: '#ffbd83' });
        case true:
        case ReleasedTypeText.RELEASED:
            return BadgeStyleRenderer({ value, color: '#ffffff', backgroundColor: '#067429' });
        default:
            return undefined;
    }
};
